import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { useKeys } from "@/lib/queries";
import {
  placeQuery,
  type BoardPlace, type WorkflowCatalog, type WorkflowDetail, type WorkflowInput, type WorkflowRun, type WorkflowRunStatus,
  type WorkflowStepCounts, type WorkflowSummary, type WorkflowVersionDetail,
} from "@/lib/workflows";

/**
 * core-api's `/workflows` (ADR-111 in platform). Admins only, as the routes are. A superadmin with
 * more than one tenant names the tenant; everybody else gets their own. Every change answers with
 * the workflow as it now is, which replaces the one in the cache, and the list is refetched for
 * its counts and badges.
 */

const own = (tenantId?: number) => tenantId ?? "own";

export function useWorkflows(tenantId?: number, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["workflows", own(tenantId)]),
    queryFn: () => api.get<WorkflowSummary[]>(`/workflows${qs({ tenant_id: tenantId })}`),
    enabled,
  });
}

export function useWorkflow(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["workflows", "detail", id ?? ""]),
    queryFn: () => api.get<WorkflowDetail>(`/workflows/${id}`),
    enabled: Boolean(id),
  });
}

/** One published version with its body (ACL-406), for naming the boxes of a lead still on it. A
 *  version never changes once published, so it is read once and kept. */
export function useWorkflowVersion(id: string, versionN: number | null, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["workflows", "version", id, versionN ?? 0]),
    queryFn: () => api.get<WorkflowVersionDetail>(`/workflows/${id}/versions/${versionN}`),
    enabled: enabled && versionN !== null,
    staleTime: Infinity,
  });
}

/** The starts, outcomes and templates the builder may offer: only what the server accepts. */
export function useWorkflowCatalog(tenantId?: number) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["workflows", "catalog", own(tenantId)]),
    queryFn: () => api.get<WorkflowCatalog>(`/workflow-catalog${qs({ tenant_id: tenantId })}`),
  });
}

/** Under the `workflows` prefix, the lists (keyed by a tenant or "own"), the leads pages and the
 *  board's counts, and not the catalog or the pause: a save changes neither, and refetching them
 *  is waste. A publish moves the counts onto the new version; taking a lead out moves its count. */
function isListOrRuns(queryKey: readonly unknown[], prefix: readonly unknown[]): boolean {
  const next = queryKey[prefix.length];
  return typeof next === "number" || next === "own" || next === "runs" || next === "counts";
}

function useSettle() {
  const keys = useKeys();
  const qc = useQueryClient();
  return (saved: WorkflowDetail) => {
    const detail = keys.scoped(["workflows", "detail", saved.id]);
    // A read of this workflow already on its way began before this write and would land on top of
    // it with the copy from before; cancelled, it puts back what it found, and the answer goes in.
    void qc.cancelQueries({ queryKey: detail, exact: true });
    qc.setQueryData(detail, saved);
    const prefix = keys.scoped(["workflows"]);
    void qc.invalidateQueries({ queryKey: prefix, predicate: (q) => isListOrRuns(q.queryKey, prefix) });
  };
}

export function useCreateWorkflow(tenantId?: number) {
  const settle = useSettle();
  return useMutation({
    mutationFn: (input: { name: string; folder: string | null }) =>
      api.post<WorkflowDetail>("/workflows", tenantId ? { ...input, tenant_id: tenantId } : input),
    onSuccess: settle,
  });
}

export function useSaveWorkflow() {
  const settle = useSettle();
  return useMutation({
    mutationFn: ({ id, ...input }: WorkflowInput & { id: string }) => api.put<WorkflowDetail>(`/workflows/${id}`, input),
    onSuccess: settle,
  });
}

/**
 * Publish, unpublish, archive or restore: each answers with the workflow as it now is. A publish
 * names the `updated_at` the page read, so a draft somebody else changed since is a 409 and not
 * published unseen.
 */
export function useWorkflowAction() {
  const settle = useSettle();
  return useMutation({
    mutationFn: ({
      id, action, updatedAt,
    }: {
      id: string;
      action: "publish" | "unpublish" | "archive" | "restore";
      updatedAt?: string;
    }) =>
      api.post<WorkflowDetail>(
        `/workflows/${id}/${action}`,
        action === "publish" && updatedAt ? { updated_at: updatedAt } : undefined,
      ),
    onSuccess: settle,
  });
}

export const RUNS_PAGE = 50;

/** The leads that went through a workflow, newest first, a page at a time; with a `place`, only
 *  the ones a count on the board stands for (ACL-407). */
export function useWorkflowRuns(
  id: string,
  filter: { status?: WorkflowRunStatus; place?: BoardPlace | null; offset: number },
) {
  const keys = useKeys();
  const where = placeQuery(filter.place);
  return useQuery({
    queryKey: keys.scoped([
      "workflows", "runs", id, filter.status ?? "all", where.at ?? "", where.step ?? "", where.path ?? "", filter.offset,
    ]),
    queryFn: () =>
      api.getPage<WorkflowRun>(
        `/workflows/${id}/runs${qs({ status: filter.status, ...where, limit: RUNS_PAGE, offset: filter.offset })}`,
      ),
    placeholderData: keepPreviousData,
  });
}

/** How many leads are on each box of the published version and went past it (ACL-407). Read with
 *  the builder and on Refresh only: nothing pushes them. */
export function useWorkflowStepCounts(id: string, enabled: boolean) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["workflows", "counts", id]),
    queryFn: () => api.get<WorkflowStepCounts>(`/workflows/${id}/step-counts`),
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useExitWorkflowRun() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workflowId, runId }: { workflowId: string; runId: string }) =>
      api.post<WorkflowRun>(`/workflows/${workflowId}/runs/${runId}/exit`),
    onSuccess: (_run, { workflowId }) => {
      const prefix = keys.scoped(["workflows"]);
      void qc.invalidateQueries({ queryKey: prefix, predicate: (q) => isListOrRuns(q.queryKey, prefix) });
      void qc.invalidateQueries({ queryKey: keys.scoped(["workflows", "detail", workflowId]) });
    },
  });
}

export function useTenantWorkflowsPaused(tenantId: number, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["workflows", "pause", tenantId]),
    queryFn: () => api.get<{ tenant_id: number; paused: boolean }>(`/tenants/${tenantId}/workflows-pause`),
    enabled,
  });
}

export function useTenantWorkflowsPause() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, paused }: { tenantId: number; paused: boolean }) =>
      api.put<{ tenant_id: number; paused: boolean }>(`/tenants/${tenantId}/workflows-pause`, { paused }),
    onSuccess: (saved) => qc.setQueryData(keys.scoped(["workflows", "pause", saved.tenant_id]), saved),
  });
}
